"use client";

import React, { useState, useEffect } from 'react';
import styles from './SideBarFilter.module.css';
import Image from 'next/image';
import { FilterType } from '@/types/filter';
import RangeBar from './Range';
import { Button } from '@mui/material';
import { useFilters } from "../../context/StateProvider";
import { useProductAllInfo, useIsFilterCleaned, useProductAllInfoSource } from '@/app/context/StateProvider';

// Define the type for sort options
export type SortOption = 'default' | 'priceLowToHigh' | 'priceHighToLow' | 'scoreLowToHigh' | 'scoreHighToLow';
export type BrandOption = 'Canon' | 'Fujifilm' | 'DJI' | 'GoPro' | 'Hasselblad' | 'Insta360' | 'Kodak' | 'Leica' | 'Nikon' | 'Olympus' | 'Panasonic' | 'Penatx' | 'Ricoh' | 'Samsung' | 'Sony' | 'all';

// Define a more specific type for a single product
type SingleProduct = {
  price: number;
  average_price_for_used: number;
  score: number;
  brand: string;
  // Add other properties as needed
};

// Redefine ProductType as an array of SingleProduct
type ProductType = SingleProduct[];
const SidebarFilter = () => {
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [values, setValues] = useState([0, 5000]);
  const [filters, updateFilters] = useFilters();
  const [productAllInfo, setProductAllInfo] = useProductAllInfo();
  const [productAllInfoSource, setProductAllInfoSource] = useProductAllInfoSource();
  const [sortOption, setSortOption] = useState<SortOption>('default');
  const [selectedBrand, setSelectedBrand] = useState<BrandOption | 'all'>('all');
  const [isSorterOpen, setIsSorterOpen] = useState(true);
  const [isBrandFilterOpen, setIsBrandFilterOpen] = useState(false);
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [isUsedOpen, setIsUsedOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isFilterCleaned, setIsFilterCleaned] = useIsFilterCleaned();

  // State for price ranges
  const [newProductPriceRange, setNewProductPriceRange] = useState({
    min: '',
    max: ''
  });
  const [usedProductPriceRange, setUsedProductPriceRange] = useState({
    min: '',
    max: ''
  });

  // State for price filters (checkboxes)
  const [newProductPriceFilters, setNewProductPriceFilters] = useState({
    under500: false,
    between500And1000: false,
    between1000And1500: false,
    over1500: false
  });
  const [usedProductPriceFilters, setUsedProductPriceFilters] = useState({
    under500: false,
    between500And1000: false,
    between1000And1500: false,
    over1500: false
  });
  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };
  const toggleSorter = () => {
    setIsSorterOpen(!isSorterOpen);
  };
  const toggleBrandFilter = () => {
    setIsBrandFilterOpen(!isBrandFilterOpen);
  };
  const toggleNewProduct = () => {
    setIsNewOpen(!isNewOpen);
  };
  const toggleUsedProduct = () => {
    setIsUsedOpen(!isUsedOpen);
  };
  const applyFilters = () => {
    let newPriceLowBar = 0;
    let newPriceHighBar = 5000;
    let usedPriceLowBar = 0;
    let usedPriceHighBar = 5000;

    // Calculate the combined range for newProductPriceFilters
    const newSelectedRanges = Object.keys(newProductPriceFilters).filter(key => newProductPriceFilters[key as keyof typeof newProductPriceFilters]);
    if (newSelectedRanges.length > 0) {
      newPriceLowBar = Math.min(...newSelectedRanges.map(key => {
        switch (key) {
          case 'under500':
            return 0;
          case 'between500And1000':
            return 500;
          case 'between1000And1500':
            return 1000;
          case 'over1500':
            return 1500;
          default:
            return 0;
        }
      }));
      newPriceHighBar = Math.max(...newSelectedRanges.map(key => {
        switch (key) {
          case 'under500':
            return 500;
          case 'between500And1000':
            return 1000;
          case 'between1000And1500':
            return 1500;
          case 'over1500':
            return 5000;
          default:
            return 5000;
        }
      }));
    } else if (newProductPriceRange.min || newProductPriceRange.max) {
      newPriceLowBar = parseInt(newProductPriceRange.min) || 0;
      newPriceHighBar = parseInt(newProductPriceRange.max) || 5000;
    }

    // Calculate the combined range for usedProductPriceFilters
    const usedSelectedRanges = Object.keys(usedProductPriceFilters).filter(key => usedProductPriceFilters[key as keyof typeof usedProductPriceFilters]);
    if (usedSelectedRanges.length > 0) {
      usedPriceLowBar = Math.min(...usedSelectedRanges.map(key => {
        switch (key) {
          case 'under500':
            return 0;
          case 'between500And1000':
            return 500;
          case 'between1000And1500':
            return 1000;
          case 'over1500':
            return 1500;
          default:
            return 0;
        }
      }));
      usedPriceHighBar = Math.max(...usedSelectedRanges.map(key => {
        switch (key) {
          case 'under500':
            return 500;
          case 'between500And1000':
            return 1000;
          case 'between1000And1500':
            return 1500;
          case 'over1500':
            return 5000;
          default:
            return 5000;
        }
      }));
    } else if (usedProductPriceRange.min || usedProductPriceRange.max) {
      usedPriceLowBar = parseInt(usedProductPriceRange.min) || 0;
      usedPriceHighBar = parseInt(usedProductPriceRange.max) || 5000;
    }
    updateFilters({
      newPriceLowBar,
      newPriceHighBar,
      usedPriceLowBar,
      usedPriceHighBar
    });
    const filteredProductAllInfo = productAllInfoSource.filter((product: ProductType) => {
      const matchResultForNew = product[0].price >= newPriceLowBar && product[0].price <= newPriceHighBar;
      const matchResultForUsed = product[0].average_price_for_used >= usedPriceLowBar && product[0].average_price_for_used <= usedPriceHighBar;
      const matchResultForBrand = selectedBrand === 'all' || product[0].brand === selectedBrand;
      return matchResultForNew && matchResultForUsed && matchResultForBrand;
    });
    console.log("filteredProductAllInfo length", filteredProductAllInfo.length);
    setProductAllInfo(filteredProductAllInfo);
    sortProducts(sortOption, filteredProductAllInfo, setProductAllInfo);
    toggleSidebar();
  };
  const sortProducts = (option: SortOption, products: ProductType[], setProducts: React.Dispatch<React.SetStateAction<ProductType[]>>) => {
    let sortedProducts = [...products];
    switch (option) {
      case 'priceLowToHigh':
        sortedProducts.sort((a, b) => a[0].price - b[0].price);
        break;
      case 'priceHighToLow':
        sortedProducts.sort((a, b) => b[0].price - a[0].price);
        break;
      case 'scoreLowToHigh':
        sortedProducts.sort((a, b) => a[0].score - b[0].score);
        break;
      case 'scoreHighToLow':
        sortedProducts.sort((a, b) => b[0].score - a[0].score);
        break;
      default:
        break;
    }
    setProducts(sortedProducts);
  };
  const handleSort = (option: SortOption) => {
    setSortOption(option);
    sortProducts(option, productAllInfo, setProductAllInfo);
  };
  const handleBrandChange = (brand: BrandOption) => {
    setSelectedBrand(brand);
  };
  const handlePriceCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, isForNewProduct: boolean) => {
    const {
      name,
      checked
    } = event.target;
    if (isForNewProduct) {
      setNewProductPriceFilters(prevState => ({
        ...prevState,
        [name]: checked
      }));
    } else {
      setUsedProductPriceFilters(prevState => ({
        ...prevState,
        [name]: checked
      }));
    }
  };
  const handlePriceInputChange = (event: React.ChangeEvent<HTMLInputElement>, isForNewProduct: boolean) => {
    const {
      name,
      value
    } = event.target;
    if (isForNewProduct) {
      setNewProductPriceRange(prevState => ({
        ...prevState,
        [name]: value
      }));
    } else {
      setUsedProductPriceRange(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
  };
  const sortOptionText: Record<SortOption, string> = {
    default: "Best Match",
    priceLowToHigh: "Price: Low to High",
    priceHighToLow: "Price: High to Low",
    scoreLowToHigh: "Score: Low to High",
    scoreHighToLow: "Score: High to Low"
  };
  const brandOptionText: Record<BrandOption | 'all', string> = {
    all: "All Brands",
    Canon: "Canon",
    Fujifilm: "Fujifilm",
    DJI: "DJI",
    GoPro: "GoPro",
    Hasselblad: "Hasselblad",
    Insta360: "Insta360",
    Kodak: "Kodak",
    Leica: "Leica",
    Nikon: "Nikon",
    Olympus: "Olympus",
    Panasonic: "Panasonic",
    Penatx: "Penatx",
    Ricoh: "Ricoh",
    Samsung: "Samsung",
    Sony: "Sony"
  };
  const newProductPriceOptionText: Record<string, string> = {
    under500: "Under $500",
    between500And1000: "$500 - $1000",
    between1000And1500: "$1000 - $1500",
    over1500: "Over $1500"
  };
  const usedProductPriceOptionText: Record<string, string> = {
    under500: "Under $500",
    between500And1000: "$500 - $1000",
    between1000And1500: "$1000 - $1500",
    over1500: "Over $1500"
  };

  // Check for mobile version
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 902);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fix the background when in mobile version
  useEffect(() => {
    if (isMobile && isSidebarVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarVisible]);
  return <div className={styles.filterContainer} data-sentry-component="SidebarFilter" data-sentry-source-file="SideBarFilter.tsx">
            <Image className={styles.filterIcon} src="/assets/filter.svg" alt="filter" width={20} height={20} onClick={toggleSidebar} onMouseEnter={e => {
      e.currentTarget.src = "/assets/filter_hovered.svg";
      e.currentTarget.className = 'w-[40px] h-[40px] cursor-pointer';
    }} onMouseOut={e => {
      e.currentTarget.src = "/assets/filter.svg";
      e.currentTarget.className = 'w-[40px] h-[40px] cursor-pointer p-1.5 border-[color:var(--white,#FFF)] rounded-lg border-2 border-solid';
    }} data-sentry-element="Image" data-sentry-source-file="SideBarFilter.tsx" />

            <div className={`${styles.sidebar} ${isSidebarVisible ? styles.sidebarVisible : ''}`}>

                <div className={styles.sidebarHeader}>
                    <div className={styles.mediaHeader}>Sorters & Filters</div>
                    <div className={styles.closeButton} onClick={toggleSidebar}>
                        {isMobile && <div className={styles.closeButtonText}>CLOSE</div>}
                        <Image className='w-[24px] h-[24px] cursor-pointer' src={isMobile ? "/assets/cancel_button.svg" : "/assets/cancel_button_white.svg"} alt="close" width={30} height={30} data-sentry-element="Image" data-sentry-source-file="SideBarFilter.tsx" />
                    </div>

                </div>

                {/* Sorter filters */}
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionHeaderText}>Sort by</div>
                        <Image className="w-5 h-5 relative cursor-pointer" src={isSorterOpen ? isMobile ? "/assets/filter_close_md.svg" : "/assets/filter_close.svg" : isMobile ? "/assets/filter_expand_md.svg" : "/assets/filter_expand.svg"} alt={isSorterOpen ? "filter_close" : "filter_expand"} width={20} height={20} onClick={toggleSorter} data-sentry-element="Image" data-sentry-source-file="SideBarFilter.tsx" />
                    </div>
                    {isSorterOpen && <div className={styles.sorterOptions}>
                            {Object.entries(sortOptionText).map(([value, text]) => <label key={value} className={styles.optionLabel}>
                                <input type="radio" name="sortOption" value={value} checked={sortOption === value} onChange={e => handleSort(e.target.value as SortOption)} className="w-5 h-5 accent-white" />
                                <span className={styles.optionText}>{text}</span>
                            </label>)}
                        </div>}
                </div>
            

                {/* New Product Price Filter */}
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionHeaderText}>Price for New Products</div>
                        <Image className="w-5 h-5 relative cursor-pointer" src={isNewOpen ? isMobile ? "/assets/filter_close_md.svg" : "/assets/filter_close.svg" : isMobile ? "/assets/filter_expand_md.svg" : "/assets/filter_expand.svg"} alt={isNewOpen ? "filter_close" : "filter_expand"} width={20} height={20} onClick={toggleNewProduct} data-sentry-element="Image" data-sentry-source-file="SideBarFilter.tsx" />
                    </div>
                    {isNewOpen && <div className={styles.sorterOptions}>
                            {Object.entries(newProductPriceOptionText).map(([key, label]) => <label key={key} className={styles.optionLabel}>
                                    <input type="checkbox" name={key} checked={newProductPriceFilters[key as keyof typeof newProductPriceFilters]} onChange={e => handlePriceCheckboxChange(e, true)} className="w-5 h-5" />
                                    <span className={styles.optionText}>{label}</span>
                                </label>)}

                            <div className={styles.priceBox}>
                                <input type="text" name="min" placeholder="$ Min" value={newProductPriceRange.min} onChange={e => handlePriceInputChange(e, true)} className={styles.priceInput} />
                                <span className="text-sm text-white">to</span>
                                <input type="text" name="max" placeholder="$ Max" value={newProductPriceRange.max} onChange={e => handlePriceInputChange(e, true)} className={styles.priceInput} />
                            </div> 

                        </div>}

                </div>
                
                {/* used */}
                <div className={styles.section}>
                     <div className={styles.sectionHeader}>
                        <div className={styles.sectionHeaderText}>Price for Used Products</div>
                        <Image className="w-5 h-5 relative cursor-pointer" src={isUsedOpen ? isMobile ? "/assets/filter_close_md.svg" : "/assets/filter_close.svg" : isMobile ? "/assets/filter_expand_md.svg" : "/assets/filter_expand.svg"} alt={isUsedOpen ? "filter_close" : "filter_expand"} width={20} height={20} onClick={toggleUsedProduct} data-sentry-element="Image" data-sentry-source-file="SideBarFilter.tsx" />
                    </div>
                    {isUsedOpen && <div className={styles.sorterOptions}>
                            {Object.entries(usedProductPriceOptionText).map(([key, label]) => <label key={key} className={styles.optionLabel}>
                                    <input type="checkbox" name={key} checked={usedProductPriceFilters[key as keyof typeof usedProductPriceFilters]} onChange={e => handlePriceCheckboxChange(e, false)} className="w-5 h-5" />
                                    <span className={styles.optionText}>{label}</span>
                                </label>)}

                            <div className={styles.priceBox}>
                                <input type="text" name="min" placeholder="$ Min" value={usedProductPriceRange.min} onChange={e => handlePriceInputChange(e, false)} className={styles.priceInput} />
                                <span className="text-sm text-white">to</span>
                                <input type="text" name="max" placeholder="$ Max" value={usedProductPriceRange.max} onChange={e => handlePriceInputChange(e, false)} className={styles.priceInput} />
                            </div> 

                        </div>}
                </div>

                {/* brand filters */}
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionHeaderText}>Brand</div>
                        <Image className="w-5 h-5 relative cursor-pointer" src={isBrandFilterOpen ? isMobile ? "/assets/filter_close_md.svg" : "/assets/filter_close.svg" : isMobile ? "/assets/filter_expand_md.svg" : "/assets/filter_expand.svg"} alt={isBrandFilterOpen ? "filter_close" : "filter_expand"} width={20} height={20} onClick={toggleBrandFilter} data-sentry-element="Image" data-sentry-source-file="SideBarFilter.tsx" />
                    </div>
                    {isBrandFilterOpen && <div className={styles.sorterOptions}>
                            {Object.entries(brandOptionText).map(([value, text]) => <label key={value} className={styles.optionLabel}>
                                <input type="radio" name="brandOption" value={value} checked={selectedBrand === value} onChange={e => handleBrandChange(e.target.value as BrandOption)} className="w-5 h-5" />
                                <span className={styles.optionText}>{text}</span>
                            </label>)}
                        </div>}
                </div>

                <div className={styles.bottomButtons}>
                    <Button variant="contained" color="primary" onClick={() => {
          setIsFilterCleaned(true);
        }} data-sentry-element="Button" data-sentry-source-file="SideBarFilter.tsx">Clear</Button>
                    <Button variant="contained" color="primary" onClick={applyFilters} data-sentry-element="Button" data-sentry-source-file="SideBarFilter.tsx">Apply</Button>
                </div>
            </div>
        </div>;
};
export default SidebarFilter;